import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import PageWithHeader from '../common/PageWithHeader';
import {InviteWithRelations, SubInviteData, usePostInviteByCodeByCodeSubInviteMutation} from '../../redux/residentPortalApi';
import InviteDetails from './InviteDetails';
import { visitorTypeOptions } from './addVisitorModel';

type VisitorType = typeof visitorTypeOptions[number]['value'];

const CONSENT_LABEL = 'I confirm the visitor has been informed their details are shared with WestProp for access control purposes';

const BnbGuestPortal: React.FC = () => {
  const [submitSubInvite, {isLoading}] = usePostInviteByCodeByCodeSubInviteMutation();
  const [code, setCode] = React.useState('');
  const [visitorName, setVisitorName] = React.useState('');
  const [visitorEmail, setVisitorEmail] = React.useState('');
  const [visitorWhatsApp, setVisitorWhatsApp] = React.useState('');
  const [purpose, setPurpose] = React.useState('');
  const [visitorType, setVisitorType] = React.useState<VisitorType>('guest');
  const [isMultiDay, setIsMultiDay] = React.useState(false);
  const [visitDateTime, setVisitDateTime] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [consentConfirmed, setConsentConfirmed] = React.useState(false);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [createdInvite, setCreatedInvite] = React.useState<InviteWithRelations | null>(null);

  const resetForm = () => {
    setVisitorName('');
    setVisitorEmail('');
    setVisitorWhatsApp('');
    setPurpose('');
    setVisitorType('guest');
    setIsMultiDay(false);
    setVisitDateTime('');
    setStartDate('');
    setEndDate('');
    setConsentConfirmed(false);
  };

  const validate = (): string | null => {
    if (code.length !== 6) {
      return 'Please enter the 6-digit  access code.';
    }

    if (!visitorName.trim()) {
      return 'Visitor name is required.';
    }

    if (visitorType === 'other' && !purpose.trim()) {
      return 'Purpose of visit is required when Visitor Type is Other.';
    }

    if (!consentConfirmed) {
      return 'Please confirm visitor consent before submitting.';
    }

    if (isMultiDay) {
      if (!startDate || !endDate) {
        return 'Start and end dates are required for multi-day invites.';
      }

      if (new Date(endDate) < new Date(startDate)) {
        return 'End date cannot be before start date.';
      }
    } else if (!visitDateTime) {
      return 'Visit date and time are required.';
    }

    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorText(null);

    const validationError = validate();
    if (validationError) {
      setErrorText(validationError);
      return;
    }

    const payload: SubInviteData = {
      visitorName: visitorName.trim(),
      visitorEmail: visitorEmail || undefined,
      visitorWhatsApp: visitorWhatsApp || undefined,
      isMultiDay,
      visitorType,
      purpose: purpose.trim(),
      visitDateTime: isMultiDay ? undefined : visitDateTime,
      startDate: isMultiDay ? startDate : undefined,
      endDate: isMultiDay ? endDate : undefined,
      consentConfirmed,
    } as SubInviteData;

    try {
      const created = await submitSubInvite({
        code,
        subInviteData: payload,
      }).unwrap();
      setCreatedInvite(created as InviteWithRelations);
      resetForm();
    } catch (error: any) {
      const msg = error?.data?.error?.message || 'Failed to create invite. Check your access code and try again.';
      setErrorText(msg);
    }
  };

  return (
    <PageWithHeader>
      <Box sx={{maxWidth: 900, mx: 'auto', p: {xs: 2, md: 4}}}>
        <Card sx={{borderRadius: 3}}>
          <CardContent>
            <Typography variant="h4" sx={{mb: 1, color: '#345678'}}> Guest Portal</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
              Guests can use their 6-digit access code to invite Company, Delivery, or Visitor guests without disturbing the property owner.
            </Typography>

            {errorText && <Alert severity="error" sx={{mb: 2}}>{errorText}</Alert>}

            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="GuestAccess Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{maxLength: 6, inputMode: 'numeric'}}
                  required
                />

                <TextField
                  label="Visitor Name"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  required
                />

                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                  <TextField
                    label="Visitor Email"
                    type="email"
                    value={visitorEmail}
                    onChange={(e) => setVisitorEmail(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Visitor WhatsApp"
                    value={visitorWhatsApp}
                    onChange={(e) => setVisitorWhatsApp(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <FormControl fullWidth>
                  <InputLabel id="bnb-visitor-type-label">Visitor Type</InputLabel>
                  <Select
                    labelId="bnb-visitor-type-label"
                    value={visitorType}
                    label="Visitor Type"
                    onChange={(e) => setVisitorType(e.target.value as VisitorType)}
                  >
                    {visitorTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Purpose of Visit"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. house cleaning, food delivery, courier pickup"
                  required={visitorType === 'other'}
                  helperText={visitorType === 'other' ? 'Required when Visitor Type is Other' : 'Optional'}
                />

                <FormControlLabel
                  control={<Switch checked={isMultiDay} onChange={(e) => setIsMultiDay(e.target.checked)} />}
                  label="Multi-day Invite"
                />

                {!isMultiDay && (
                  <TextField
                    label="Visit Date & Time"
                    type="datetime-local"
                    value={visitDateTime}
                    onChange={(e) => setVisitDateTime(e.target.value)}
                    InputLabelProps={{shrink: true}}
                    required
                  />
                )}

                {isMultiDay && (
                  <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{shrink: true}}
                      required
                      fullWidth
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{shrink: true}}
                      required
                      fullWidth
                    />
                  </Stack>
                )}

                <Box
                  sx={{
                    border: '1px solid #eaeef2',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: consentConfirmed ? '#f4f9f6' : '#fffaf0',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={consentConfirmed}
                        onChange={(e) => setConsentConfirmed(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography variant="body2">{CONSENT_LABEL}</Typography>}
                  />
                </Box>

                <Stack direction="row" spacing={2}>
                  <Button type="submit" variant="contained" disabled={isLoading}>
                    {isLoading ? 'Creating Invite...' : 'Create Invite'}
                  </Button>
                  <Button type="button" variant="outlined" onClick={resetForm}>
                    Reset
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>

        {createdInvite && (
          <Card sx={{borderRadius: 3, mt: 3}}>
            <CardContent>
              <Typography variant="h6" sx={{mb: 1, color: '#345678'}}>Invite Created</Typography>
              <InviteDetails invite={createdInvite} showDownloadBtn={true} showBanner={false} />
            </CardContent>
          </Card>
        )}
      </Box>
    </PageWithHeader>
  );
};

export default BnbGuestPortal;
